import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import './UserList.css';

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('nb-NO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('nb-NO', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const filterSubscriptionStatuses = (statusesString) => {
    if (!statusesString) return [];

    const statuses = statusesString.split(',').map((s) => s.trim().toLowerCase());
    const filtered = [...statuses];

    if (filtered.includes('active') && filtered.includes('pending')) {
        filtered.splice(filtered.indexOf('pending'), 1);
    }
    if (filtered.includes('trialing') && filtered.includes('pending')) {
        filtered.splice(filtered.indexOf('pending'), 1);
    }
    if (filtered.includes('active') && filtered.includes('canceled')) {
        filtered.splice(filtered.indexOf('canceled'), 1);
    }

    return statusesString
        .split(',')
        .map((s) => s.trim())
        .filter((status) => filtered.includes(status.toLowerCase()));
};

const getActiveSubscriptionCount = (statusesString) => {
    const statuses = filterSubscriptionStatuses(statusesString);
    return statuses.filter((status) => {
        const lower = status.toLowerCase();
        return lower === 'active' || lower === 'trialing';
    }).length;
};

const getDevicePillClass = (device) => {
    const normalized = (device || '').toLowerCase();
    if (normalized === 'android') return 'user-list-pill--device-android';
    if (normalized === 'ios') return 'user-list-pill--device-ios';
    return 'user-list-pill--device-unknown';
};

export default function UserList() {
    const [users, setUsers] = useState([]);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [userRole, setUserRole] = useState(null);
    const [deviceFilter, setDeviceFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [levelFilter, setLevelFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [createdFrom, setCreatedFrom] = useState('');
    const [createdTo, setCreatedTo] = useState('');
    const navigate = useNavigate();
    const { user, logout } = useAuth();

    useEffect(() => {
        fetchUserRole();
        fetchUsers();
    }, []);

    const fetchUserRole = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/verify`,
                { withCredentials: true }
            );
            setUserRole(response.data.role);
        } catch (err) {
            console.error('Error fetching user role:', err);
        }
    };

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/users`,
                { withCredentials: true }
            );
            setUsers(response.data);
            setError('');
        } catch (err) {
            console.error('Error fetching users:', err);
            if (err.response?.status === 401) {
                logout();
            } else {
                setError('Kunne ikke laste brukere');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleUserClick = (userId) => {
        navigate(`/users/${userId}`);
    };

    const isSuperAdmin = userRole === 'superadmin';

    const levelOptions = useMemo(() => {
        const map = new Map();
        let hasUnset = false;

        users.forEach((u) => {
            if (u.level_idlevel) {
                map.set(u.level_idlevel, u.levelName || `Nivå ${u.level_idlevel}`);
            } else {
                hasUnset = true;
            }
        });

        const options = Array.from(map.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([id, name]) => ({ id: String(id), name }));

        if (hasUnset) {
            options.push({ id: 'unset', name: 'Ikke satt' });
        }

        return options;
    }, [users]);

    const filteredUsers = users.filter((row) => {
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            const nameMatch = row.name?.toLowerCase().includes(searchLower);
            const emailMatch = row.email?.toLowerCase().includes(searchLower);
            const phoneMatch = row.phone_number?.toLowerCase().includes(searchLower);
            if (!nameMatch && !emailMatch && !phoneMatch) return false;
        }

        if (createdFrom || createdTo) {
            if (!row.created_at) return false;
            const createdDate = new Date(row.created_at);
            if (Number.isNaN(createdDate.getTime())) return false;
            if (createdFrom) {
                const fromDate = new Date(`${createdFrom}T00:00:00`);
                if (createdDate < fromDate) return false;
            }
            if (createdTo) {
                const toDate = new Date(`${createdTo}T23:59:59.999`);
                if (createdDate > toDate) return false;
            }
        }

        if (deviceFilter !== 'all') {
            const device = row.device || 'unknown';
            if (device !== deviceFilter) return false;
        }

        if (levelFilter !== 'all') {
            if (levelFilter === 'unset') {
                if (row.level_idlevel) return false;
            } else if (String(row.level_idlevel) !== levelFilter) {
                return false;
            }
        }

        if (statusFilter !== 'all') {
            if (statusFilter === 'no-subscription') {
                if (row.subscriptionStatuses) return false;
            } else {
                if (!row.subscriptionStatuses) return false;
                const userStatuses = row.subscriptionStatuses
                    .split(',')
                    .map((s) => s.trim().toLowerCase());
                if (!userStatuses.includes(statusFilter.toLowerCase())) return false;
            }
        }

        return true;
    });

    const deviceCounts = users.reduce((counts, row) => {
        const device = row.device || 'unknown';
        counts[device] = (counts[device] || 0) + 1;
        return counts;
    }, {});

    const androidCount = deviceCounts.android || 0;
    const iosCount = deviceCounts.ios || 0;

    if (loading) {
        return <div className="user-list-loading">Laster brukere…</div>;
    }

    return (
        <div className="user-list">
            <div className="user-list-container">
                <div className="user-list-top">
                    <Link to="/dashboard" className="user-list-back">
                        <i className="fas fa-arrow-left" aria-hidden="true" />
                        Tilbake til hovedsiden
                    </Link>
                    {user?.businessAdminFor && (
                        <Link
                            to="/businesses/current"
                            className="user-list-back user-list-back--primary"
                        >
                            <i className="fas fa-building" aria-hidden="true" />
                            Gå til min bedrift
                        </Link>
                    )}
                </div>

                <header className="user-list-header">
                    <div className="user-list-header-text">
                        <h1>{isSuperAdmin ? 'Alle brukere' : 'Brukere i din bedrift'}</h1>
                        <p>Oversikt over brukere med nivå, abonnement og aktivitet.</p>
                    </div>
                    <button type="button" className="user-list-logout" onClick={logout}>
                        <i className="fas fa-sign-out-alt" aria-hidden="true" />
                        Logg ut
                    </button>
                </header>

                {error && <div className="user-list-error">{error}</div>}

                <div className="user-list-stats">
                    <span className="user-list-stat">
                        <i className="fab fa-android" aria-hidden="true" />
                        Android: {androidCount}
                    </span>
                    <span className="user-list-stat">
                        <i className="fab fa-apple" aria-hidden="true" />
                        iOS: {iosCount}
                    </span>
                    <span className="user-list-stat">
                        <i className="fas fa-users" aria-hidden="true" />
                        Totalt: {users.length}
                    </span>
                    <span className="user-list-stat">
                        <i className="fas fa-filter" aria-hidden="true" />
                        Vist: {filteredUsers.length}
                    </span>
                </div>

                <div className="user-list-filters">
                    <div className="user-list-filter user-list-filter--wide">
                        <label htmlFor="searchTerm">Søk</label>
                        <input
                            type="search"
                            id="searchTerm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Navn, e-post eller telefon…"
                        />
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="levelFilter">Nivå</label>
                        <select
                            id="levelFilter"
                            value={levelFilter}
                            onChange={(e) => setLevelFilter(e.target.value)}
                        >
                            <option value="all">Alle nivåer</option>
                            {levelOptions.map((option) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="deviceFilter">Enhet</label>
                        <select
                            id="deviceFilter"
                            value={deviceFilter}
                            onChange={(e) => setDeviceFilter(e.target.value)}
                        >
                            <option value="all">Alle enheter</option>
                            <option value="android">Android</option>
                            <option value="ios">iOS</option>
                            <option value="unknown">Ukjent</option>
                        </select>
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="statusFilter">Abonnement</label>
                        <select
                            id="statusFilter"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">Alle statuser</option>
                            <option value="active">Aktiv</option>
                            <option value="trialing">Prøveperiode</option>
                            <option value="pending">Venter</option>
                            <option value="canceled">Kansellert</option>
                            <option value="past_due">Forfalt</option>
                            <option value="unpaid">Ubetalt</option>
                            <option value="no-subscription">Ingen abonnement</option>
                        </select>
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="createdFrom">Registrert fra</label>
                        <input
                            type="date"
                            id="createdFrom"
                            value={createdFrom}
                            onChange={(e) => setCreatedFrom(e.target.value)}
                        />
                    </div>

                    <div className="user-list-filter">
                        <label htmlFor="createdTo">Registrert til</label>
                        <input
                            type="date"
                            id="createdTo"
                            value={createdTo}
                            onChange={(e) => setCreatedTo(e.target.value)}
                        />
                    </div>
                </div>

                {filteredUsers.length > 0 ? (
                    <div className="user-list-table-wrap">
                        <table className="user-list-table">
                            <thead>
                                <tr>
                                    <th>Bruker</th>
                                    <th>Nivå</th>
                                    <th>Enhet</th>
                                    <th>Aktive abonn.</th>
                                    <th>Abonnement</th>
                                    <th>Aktiviteter</th>
                                    <th>Program</th>
                                    <th>Sist innlogget</th>
                                    <th>Opprettet</th>
                                    {isSuperAdmin && <th>Bedrift</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((row) => {
                                    const filteredStatuses = filterSubscriptionStatuses(
                                        row.subscriptionStatuses
                                    );
                                    const activeCount = getActiveSubscriptionCount(
                                        row.subscriptionStatuses
                                    );
                                    const deviceLabel = row.device
                                        ? row.device.toLowerCase()
                                        : 'Ukjent';

                                    return (
                                        <tr
                                            key={row.idusers}
                                            className="user-list-row"
                                            onClick={() => handleUserClick(row.idusers)}
                                        >
                                            <td>
                                                <span className="user-list-name">
                                                    {row.name || '—'}
                                                </span>
                                                <span className="user-list-email">
                                                    {row.email || '—'}
                                                </span>
                                                {row.phone_number && (
                                                    <span className="user-list-phone">
                                                        {row.phone_number}
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className="user-list-pill user-list-pill--level">
                                                    {row.levelName || 'Ikke satt'}
                                                </span>
                                            </td>
                                            <td>
                                                <span
                                                    className={`user-list-pill ${getDevicePillClass(row.device)}`}
                                                >
                                                    {deviceLabel}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="user-list-num">
                                                    {activeCount}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="user-list-statuses">
                                                    {filteredStatuses.length > 0 ? (
                                                        filteredStatuses.map((status) => (
                                                            <span
                                                                key={status}
                                                                className={`user-list-status ${status.trim().toLowerCase()}`}
                                                            >
                                                                {status.trim()}
                                                            </span>
                                                        ))
                                                    ) : (
                                                        <span className="user-list-status no-subscription">
                                                            Ingen
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <span className="user-list-num">
                                                    {row.totalCarriedOutCount ?? 0}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="user-list-num">
                                                    {row.activeProgramCount ?? 0}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="user-list-date">
                                                    {formatDateTime(row.last_logged_in)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="user-list-date">
                                                    {formatDate(row.created_at)}
                                                </span>
                                            </td>
                                            {isSuperAdmin && (
                                                <td>
                                                    {row.businessName || '—'}
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="user-list-empty">
                        Ingen brukere funnet med valgte filtre.
                    </p>
                )}
            </div>
        </div>
    );
}
